import React from "react";
import { useDispatch } from "react-redux";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { object, string, ref } from "yup";
import { duplicateUser, resetStatus } from "../redux/usersSagas";
import { MainModal } from "../../../../components/modal/MainModal";
import { ErrorList } from "./ErrorList";
import { useError } from "../hooks/useError";
import LoadingAnimation from "../../../../components/loadingAnimation/loadingAnimation";
import ToggleableButton from "../../../../components/toggleableButton/toggleableButton";
import { showToast } from "../../../../utils/showToast";
import { Input, PasswordInput } from "../../../../components/Input.js/Input";
import { generatePassword, passwordSchemaTest } from "../../../../utils/password-utils";

const schema = object({
  first_name: string().label("First Name").min(2).required(),
  last_name: string().label("Last Name").min(2).required(),
  email: string().label("Email").email().required(),
  password: string().label("Password").min(8).max(40).test(passwordSchemaTest).required(),
  password_confirmation: string()
    .label("Password Confirmation")
    .oneOf([ref("password")], "${path} must match Password")
    .required()
}).required();

const generatedPassword = generatePassword();

export const Duplicate = ({ isOpen, onClose, selected: { id, name, orgName } }) => {
  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting }
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      send_welcome_email: true,
      password: generatedPassword,
      password_confirmation: generatedPassword
    }
  });

  const dispatch = useDispatch();

  const close = () => {
    dispatch(resetStatus());
    onClose();
  };

  const { errors: serverErrors, status } = useError(() => {
    showToast(`${name} user was successfully duplicated to new user`);
    close();
  });

  const primaryButtonDisabled = isSubmitting || status == "pending";

  const onSubmit = (data) => {
    dispatch(duplicateUser({ data, id, orgName }));
  };

  return (
    <>
      <MainModal
        isOpen={isOpen}
        onClose={close}
        parentSelector=".admin-users"
        headerText="Duplicate User"
        headerCloseHandler={close}
        bodyClassName="main-popup-body"
        primaryButtonText="Save"
        primaryButtonClass="primary"
        primaryButtonDisabled={primaryButtonDisabled}
        primaryBtnHandler={handleSubmit(onSubmit)}
        secondaryBtnText="Cancel"
        secondaryBtnClass="secondary"
        secondaryBtnHandler={close}
        height="auto"
        width={900}
        isAdminPanel={false}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="admin-users__section">
            <Input label="First Name" htmlFor="first_name" customProps={register("first_name")} />
            <Input label="Last Name" htmlFor="last_name" customProps={register("last_name")} />
            <Input label="Email" htmlFor="email" customProps={register("email")} />
          </div>

          <div className="admin-users__section">
            <PasswordInput label="Password" customProps={register("password")} htmlFor="password" />
            <PasswordInput
              label="Password Confirmation"
              customProps={register("password_confirmation")}
              htmlFor="password_confirmation"
            />

            <div className="switch">
              <p className="switch__title">Send Welcome Email</p>
              <Controller
                control={control}
                name="send_welcome_email"
                render={({ field: { onChange, value } }) => {
                  return (
                    <div className={`switch__content ${value ? "switch__content--active" : ""}`}>
                      <ToggleableButton
                        isRadio
                        title="showArchiveList"
                        isActive={value}
                        onClick={(isActive) => onChange(isActive)}
                      />
                    </div>
                  );
                }}
              />
            </div>
          </div>

          {status === "pending" && <LoadingAnimation uiLoader />}
          <ErrorList errors={{ ...errors, ...serverErrors }} />
        </form>
      </MainModal>
    </>
  );
};
